import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import secureApiService from '../services/secureApi';
import { formatShortDateTime } from '../utils/dateFormatter';

const USD_TO_SAR = 3.75; // Fixed exchange rate

const AgentTracePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);
  const [traces, setTraces] = useState([]);
  const [traceStats, setTraceStats] = useState({
    monthlyCostUSD: 0,
    averageConversationCostUSD: 0,
    totalApiCalls: 0
  });
  const [showCostColumn, setShowCostColumn] = useState(true);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchTraceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const fetchTraceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await secureApiService.getAgentTrace({ limit });
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.traces)
          ? response.traces
          : [];
      setTraces(data);
      setTraceStats(response?.stats || {
        monthlyCostUSD: 0,
        averageConversationCostUSD: 0,
        totalApiCalls: 0
      });
    } catch (err) {
      console.error('Failed to load agent trace', err);
      const message = err.response?.data?.message || 'تعذر جلب بيانات المساعد';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const userMessages = traces.filter((item) => item.role === 'user').length;
    const assistantMessages = traces.filter((item) => item.role === 'assistant').length;
    const opsMessages = traces.filter((item) => item.role === 'ops_agent').length;
    const intents = traces
      .map((item) => item.intent)
      .filter(Boolean);
    const uniqueIntents = new Set(intents).size;

    return {
      total: traces.length,
      userMessages,
      assistantMessages,
      opsMessages,
      uniqueIntents
    };
  }, [traces]);

  const formatTimestamp = (timestamp) => (timestamp ? formatShortDateTime(timestamp) : '—');

  const formatContent = (content) => {
    if (!content) return '—';
    if (content.length <= 160) return content;
    return `${content.slice(0, 160)}…`;
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '—';

    // For very small costs (< $0.01), show up to 6 decimal places for visibility in testing
    if (value < 0.01) {
      return `$${value.toFixed(6)}`;
    }

    // For costs >= $0.01, show standard 2 decimal places
    return `$${value.toFixed(2)}`;
  };

  const formatCurrencyWithSAR = (usdValue) => {
    if (typeof usdValue !== 'number' || Number.isNaN(usdValue)) return '—';

    const sarValue = usdValue * USD_TO_SAR;
    const usdFormatted = usdValue < 0.01 ? `$${usdValue.toFixed(6)}` : `$${usdValue.toFixed(2)}`;
    const sarFormatted = sarValue.toLocaleString('ar-SA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return (
      <>
        <div>{usdFormatted}</div>
        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
          {sarFormatted} ر.س
        </div>
      </>
    );
  };

  const formatSlotValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${val}`)
        .join(', ');
    }
    return String(value);
  };

  const renderWorkflowDetails = (workflow) => {
    if (!workflow) return null;

    const slotEntries = workflow.slots && Object.keys(workflow.slots).length > 0
      ? Object.entries(workflow.slots)
      : [];

    const actions = Array.isArray(workflow.actions) ? workflow.actions : [];

    const statusBadgeClass = (status) => {
      switch (status) {
        case 'succeeded':
          return 'bg-success';
        case 'failed':
          return 'bg-danger';
        default:
          return 'bg-warning text-dark';
      }
    };

    return (
      <div className="mt-2">
        {workflow.safetyFlag && (
          <div className="text-danger fw-semibold">
            ⚠️ تنبيه سلامة
          </div>
        )}
        {slotEntries.length > 0 && (
          <div className="mb-2">
            <div className="text-muted small mb-1">الحقول المكتملة:</div>
            <div className="d-flex flex-wrap gap-1">
              {slotEntries.map(([slotKey, slotValue]) => (
                <span key={slotKey} className="badge bg-light text-dark border">
                  {slotKey}: {formatSlotValue(slotValue)}
                </span>
              ))}
            </div>
          </div>
        )}
        {actions.length > 0 && (
          <div>
            <div className="text-muted small mb-1">الإجراءات:</div>
            <div className="d-flex flex-column gap-1">
              {actions.map((action, index) => (
                <div key={`${action.type}-${index}`} className="border rounded px-2 py-1">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="badge bg-dark">{action.type}</span>
                    <span className={`badge ${statusBadgeClass(action.status)}`}>
                      {action.status === 'succeeded'
                        ? 'تمت'
                        : action.status === 'failed'
                        ? 'فشلت'
                        : 'قيد التنفيذ'}
                    </span>
                    {action.referenceId && (
                      <span className="text-muted small">#{action.referenceId}</span>
                    )}
                    {typeof action.latencyMs === 'number' && (
                      <span className="text-muted small">⏱ {action.latencyMs} مللي ثانية</span>
                    )}
                  </div>
                  {action.errorMessage && (
                    <div className="text-danger small mt-1">{action.errorMessage}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DesktopLayout activeNavItem="agent-trace" headerTitle="تتبع المساعد الذكي">
      <div className="container-fluid" dir="rtl">
          <div className="card mb-3 shadow-sm">
          <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h4 className="mb-1">سجل نشاط المساعد الذكي</h4>
              <p className="text-muted mb-0">
                عرض آخر {stats.total} رسالة مع حالة الغرض والبيانات التي تم استخدامها
              </p>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <select
                className="form-select"
                style={{ minWidth: '140px' }}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {[25, 50, 100, 150, 200].map((value) => (
                  <option key={value} value={value}>
                    آخر {value} رسالة
                  </option>
                ))}
              </select>
              <button className="btn btn-primary" onClick={fetchTraceData} disabled={loading}>
                {loading ? 'جاري التحديث...' : 'تحديث السجل'}
              </button>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="costToggle"
                  checked={showCostColumn}
                  onChange={(e) => setShowCostColumn(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="costToggle">
                  عرض التكلفة
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">إجمالي الرسائل</p>
                <h3 className="mb-0">{stats.total}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">رسائل الضيوف</p>
                <h3 className="mb-0">{stats.userMessages}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">ردود المساعد</p>
                <h3 className="mb-0">{stats.assistantMessages}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">رسائل فريق العمليات</p>
                <h3 className="mb-0">{stats.opsMessages}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">عدد النوايا المكتشفة</p>
                <h3 className="mb-0">{stats.uniqueIntents}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">تكلفة الذكاء الاصطناعي (آخر ٣٠ يوماً)</p>
                <h3 className="mb-0">{formatCurrencyWithSAR(traceStats.monthlyCostUSD)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">متوسط التكلفة لكل محادثة</p>
                <h3 className="mb-0">{formatCurrencyWithSAR(traceStats.averageConversationCostUSD)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card stat-card">
              <div className="card-body">
                <p className="text-muted mb-1">عدد مكالمات الـ LLM</p>
                <h3 className="mb-0">{traceStats.totalApiCalls?.toLocaleString('en-US') || 0}</h3>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="card shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="mt-3 mb-0">جاري تحميل سجل المساعد...</p>
              </div>
            ) : traces.length === 0 ? (
              <p className="text-muted mb-0">لا توجد سجلات متاحة حالياً.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>الوقت</th>
                      <th>الهاتف</th>
                      <th>العقار</th>
                      <th>الدور</th>
                      <th style={{ width: '35%' }}>نص الرسالة</th>
                      <th>الغرض</th>
                      {showCostColumn && <th>التكلفة</th>}
                      <th>البيانات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traces.map((entry) => (
                      <tr key={entry.messageId}>
                        <td className="text-muted small">{formatTimestamp(entry.timestamp)}</td>
                        <td>{entry.conversation?.guestPhone || '—'}</td>
                        <td>
                          <strong>{entry.property?.name || '—'}</strong>
                          <div className="text-muted small">
                            {entry.property?.city || ''}
                            {(entry.property?.city && entry.conversation?.bookingId) ? ' | ' : ''}
                            {entry.conversation?.bookingConfirmation
                              ? `حجز: ${entry.conversation.bookingConfirmation}`
                              : entry.conversation?.bookingId
                                ? `حجز: ${entry.conversation.bookingId}`
                                : ''}
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const role = entry.role;
                            if (role === 'assistant') {
                              return <span className="badge bg-success">المساعد</span>;
                            }
                            if (role === 'ops_agent') {
                              return <span className="badge bg-warning text-dark">فريق العمليات</span>;
                            }
                            if (role === 'system') {
                              return <span className="badge bg-secondary">النظام</span>;
                            }
                            return <span className="badge bg-info text-dark">الضيف</span>;
                          })()}
                        </td>
                        <td>
                          <div className="fw-semibold">{formatContent(entry.content)}</div>
                        </td>
                        <td>
                          {(() => {
                            const traceIntent = entry.metadata?.traceMetadata?.intent;
                            const displayIntent = traceIntent || entry.intent;
                            const activeWorkflow = entry.metadata?.traceMetadata?.activeWorkflow;

                            if (!displayIntent) {
                              return <span className="text-muted">—</span>;
                            }

                            return (
                              <>
                                <span className="badge bg-dark">{displayIntent}</span>
                                {activeWorkflow && traceIntent && activeWorkflow !== traceIntent && (
                                  <div className="text-muted small mt-1">
                                    سير العمل: {activeWorkflow}
                                  </div>
                                )}
                                {entry.confidence && (
                                  <div className="text-muted small mt-1">{Math.round(entry.confidence * 100)}%</div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        {showCostColumn && (
                          <td className="text-muted small">
                            {typeof entry.metadata?.estimatedCostUSD === 'number'
                              ? formatCurrency(entry.metadata.estimatedCostUSD)
                              : '—'}
                          </td>
                        )}
                        <td className="text-muted small">
                          {entry.metadata?.tokensUsed && <div>Tokens: {entry.metadata.tokensUsed}</div>}
                          {entry.metadata?.aiCalls && <div>LLM Calls: {entry.metadata.aiCalls}</div>}
                          {entry.metadata?.responseTime && <div>زمن الاستجابة: {entry.metadata.responseTime} مللي ثانية</div>}
                          {renderWorkflowDetails(entry.metadata?.workflow)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};

export default AgentTracePage;
